<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="page-header">
        <h1>趁鲜药材管理</h1>
        <div class="header-actions">
          <span class="total-count">共 {{ currentTotal }} 条记录</span>
          <input type="file" ref="excelInput" accept=".xlsx,.xls" @change="handleExcelUpload" style="display: none" />
          <button class="upload-btn" @click="$refs.excelInput.click()" v-if="currentTab === 'companies'">
            <span>📤</span> 导入Excel
          </button>
          <button class="add-btn" @click="handleAdd">
            <span>➕</span> 添加数据
          </button>
        </div>
      </div>

      <div class="tabs">
        <button :class="['tab', { active: currentTab === 'companies' }]" @click="switchTab('companies')">
          加工企业 ({{ companiesTotal }})
        </button>
        <button :class="['tab', { active: currentTab === 'catalog' }]" @click="switchTab('catalog')">
          药材目录 ({{ catalogTotal }})
        </button>
      </div>

      <div class="filter-bar">
        <input type="text" v-model="searchQuery" class="search-input" placeholder="搜索..." @keyup.enter="fetchData" />
        <button class="search-btn" @click="fetchData">搜索</button>
      </div>

      <div class="content-body">
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>

        <!-- 加工企业表格 -->
        <table class="data-table" v-else-if="currentTab === 'companies'">
          <thead>
            <tr>
              <th>ID</th>
              <th>地区</th>
              <th>公司名称</th>
              <th>药材</th>
              <th>发布时间</th>
              <th>发布单位</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in companiesList" :key="item.id">
              <td>{{ item.id }}</td>
              <td><span class="region-tag">{{ item.region }}</span></td>
              <td class="company-name">{{ item.company_name }}</td>
              <td><span class="herb-names">{{ item.herb_names || '-' }}</span></td>
              <td>{{ item.publish_time || '-' }}</td>
              <td>{{ item.publisher || '-' }}</td>
              <td class="actions">
                <button class="edit-btn" @click="editItem(item)">编辑</button>
                <button class="delete-btn" @click="deleteItem(item.id)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 药材目录表格（按省份聚合） -->
        <table class="data-table" v-else-if="currentTab === 'catalog'">
          <thead>
            <tr>
              <th>省份</th>
              <th>品种</th>
              <th>数量</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in catalogList" :key="item.province">
              <td><span class="province-tag">{{ item.province }}</span></td>
              <td><span class="herb-names">{{ item.herb_names || '-' }}</span></td>
              <td><span class="count-tag">{{ item.herb_count || 0 }} 种</span></td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td class="actions">
                <button class="edit-btn" @click="editCatalogItem(item)">编辑</button>
                <button class="delete-btn" @click="deleteCatalogItem(item.province)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">上一页</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">下一页</button>
        </div>
      </div>
    </div>

    <!-- 添加/编辑模态框 -->
    <div class="modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ editingId ? '编辑' : '添加' }}{{ tabTitle }}</h2>
          <button class="close-btn" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <!-- 加工企业表单 -->
          <div v-if="currentTab === 'companies'">
            <div class="form-row">
              <div class="form-item">
                <label>地区 *</label>
                <input type="text" v-model="form.region" required placeholder="如：甘肃省" />
              </div>
              <div class="form-item">
                <label>公司名称 *</label>
                <input type="text" v-model="form.company_name" required placeholder="公司名称" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>发布时间</label>
                <input type="text" v-model="form.publish_time" placeholder="如：2024-01-15" />
              </div>
              <div class="form-item">
                <label>发布单位</label>
                <input type="text" v-model="form.publisher" placeholder="如：甘肃省药品监督管理局" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item full-width">
                <label>药材 <span class="form-hint">（用顿号、逗号或空格分隔）</span></label>
                <textarea v-model="form.herb_names" rows="3" placeholder="如：党参、当归、黄芪"></textarea>
              </div>
            </div>
          </div>

          <!-- 药材目录表单（按省份聚合） -->
          <div v-else-if="currentTab === 'catalog'">
            <div class="form-row">
              <div class="form-item">
                <label>省份 *</label>
                <input type="text" v-model="catalogForm.province" required placeholder="如：云南省" :disabled="catalogEditingProvince" />
                <span class="form-hint" v-if="catalogEditingProvince">（编辑时省份不可修改）</span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item full-width">
                <label>药材名 * <span class="form-hint">（用顿号、逗号或空格分隔）</span></label>
                <textarea v-model="catalogForm.herb_names" rows="5" placeholder="如：党参、当归、黄芪、甘草"></textarea>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showModal = false">取消</button>
          <button class="submit-btn" @click="submitForm" :disabled="submitting">
            {{ submitting ? '提交中...' : '提交' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import { getAdminPassword } from '../utils/api'
import axios from 'axios'
import * as XLSX from 'xlsx'

export default {
  name: 'XianxianHerbsList',
  components: { Sidebar },
  data() {
    return {
      currentTab: 'companies',
      loading: true,
      error: '',
      searchQuery: '',
      currentPage: 1,
      pageSize: 20,
      
      // 各表数据
      companiesList: [],
      companiesTotal: 0,
      catalogList: [],
      catalogTotal: 0,
      
      // 加工企业模态框
      showModal: false,
      editingId: null,
      submitting: false,
      form: {},
      
      // 药材目录模态框（单独处理）
      catalogEditingProvince: null,
      catalogForm: {},
      
      // 上传状态
      uploading: false
    }
  },
  computed: {
    currentTotal() {
      if (this.currentTab === 'companies') return this.companiesTotal
      if (this.currentTab === 'catalog') return this.catalogTotal
      return 0
    },
    totalPages() {
      return Math.ceil(this.currentTotal / this.pageSize)
    },
    tabTitle() {
      const titles = {
        companies: '加工企业',
        catalog: '药材目录'
      }
      return titles[this.currentTab] || ''
    },
    apiEndpoint() {
      const endpoints = {
        companies: '/api/admin/xianxian-companies',
        catalog: '/api/admin/xianxian-catalog'
      }
      return endpoints[this.currentTab] || ''
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    switchTab(tab) {
      this.currentTab = tab
      this.currentPage = 1
      this.searchQuery = ''
      this.fetchData()
    },
    async fetchData() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          search: this.searchQuery
        }
        const res = await axios.get(this.apiEndpoint, {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (res.data?.code === 200) {
          const data = res.data.data
          if (this.currentTab === 'companies') {
            this.companiesList = data?.list || []
            this.companiesTotal = data?.total || 0
          } else if (this.currentTab === 'catalog') {
            this.catalogList = data?.list || []
            this.catalogTotal = data?.total || 0
          }
        } else {
          this.error = res.data?.msg || '获取失败'
        }
      } catch (err) {
        this.error = '获取数据失败'
        console.error('Error:', err)
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      this.currentPage = page
      this.fetchData()
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('zh-CN')
    },
    handleAdd() {
      if (this.currentTab === 'companies') {
        this.editingId = null
        this.form = {}
      } else if (this.currentTab === 'catalog') {
        this.catalogEditingProvince = null
        this.catalogForm = {}
      }
      this.showModal = true
    },
    editItem(item) {
      this.editingId = item.id
      this.form = { ...item }
      this.showModal = true
    },
    editCatalogItem(item) {
      this.catalogEditingProvince = item.province
      this.catalogForm = {
        province: item.province,
        herb_names: item.herb_names
      }
      this.showModal = true
    },
    async deleteItem(id) {
      if (!confirm('确定删除？')) return
      try {
        const res = await axios.delete(`${this.apiEndpoint}/${id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (res.data?.code === 200) {
          this.fetchData()
        } else {
          alert('删除失败：' + res.data?.msg)
        }
      } catch (err) {
        alert('删除失败')
      }
    },
    async deleteCatalogItem(province) {
      if (!confirm('确定删除该省份的所有药材？')) return
      try {
        const res = await axios.delete(`/api/admin/xianxian-catalog/${encodeURIComponent(province)}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (res.data?.code === 200) {
          this.fetchData()
        } else {
          alert('删除失败：' + res.data?.msg)
        }
      } catch (err) {
        alert('删除失败')
      }
    },
    async submitForm() {
      this.submitting = true
      try {
        let res
        
        if (this.currentTab === 'catalog') {
          // 药材目录单独处理
          if (this.catalogEditingProvince) {
            res = await axios.put(`/api/admin/xianxian-catalog/${encodeURIComponent(this.catalogEditingProvince)}`, this.catalogForm, {
              headers: { 'x-admin-password': getAdminPassword() }
            })
          } else {
            res = await axios.post('/api/admin/xianxian-catalog', this.catalogForm, {
              headers: { 'x-admin-password': getAdminPassword() }
            })
          }
        } else {
          // 加工企业
          if (this.editingId) {
            res = await axios.put(`${this.apiEndpoint}/${this.editingId}`, this.form, {
              headers: { 'x-admin-password': getAdminPassword() }
            })
          } else {
            res = await axios.post(this.apiEndpoint, this.form, {
              headers: { 'x-admin-password': getAdminPassword() }
            })
          }
        }
        
        if (res.data?.code === 200) {
          this.showModal = false
          this.fetchData()
        } else {
          alert('提交失败：' + res.data?.msg)
        }
      } catch (err) {
        alert('提交失败')
      } finally {
        this.submitting = false
      }
    },
    async handleExcelUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      this.uploading = true
      try {
        const data = await file.arrayBuffer()
        const workbook = XLSX.read(data, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 })
        
        // 获取列名（第一行）
        const headers = rows[0] || []
        const regionIdx = headers.findIndex(h => String(h).includes('地区'))
        const timeIdx = headers.findIndex(h => String(h).includes('公布时间') || String(h).includes('时间'))
        const companyIdx = headers.findIndex(h => String(h).includes('企业') || String(h).includes('公司'))
        const herbIdx = headers.findIndex(h => String(h).includes('品种') || String(h).includes('药材'))
        const publisherIdx = headers.findIndex(h => String(h).includes('监督管理局') || String(h).includes('监管') || String(h).includes('发布单位'))
        
        if (companyIdx === -1 || herbIdx === -1) {
          alert('Excel格式错误：缺少企业名称或药材品种列')
          return
        }
        
        // Excel 日期单元格默认解析为序列号数字(如 45579 = 2024-10-30),
        // 需要手动转换为日期字符串;文本型日期则清理换行。
        const formatExcelDate = (v) => {
          if (v === null || v === undefined || v === '') return ''
          if (v instanceof Date && !isNaN(v)) {
            return `${v.getFullYear()}年${v.getMonth() + 1}月${v.getDate()}日`
          }
          if (typeof v === 'number' && isFinite(v)) {
            const d = new Date(Math.round((v - 25569) * 86400 * 1000))
            if (!isNaN(d)) {
              return `${d.getUTCFullYear()}年${d.getUTCMonth() + 1}月${d.getUTCDate()}日`
            }
          }
          return String(v).replace(/\r?\n/g, '').trim()
        }

        // 解析数据
        const importData = []
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i]
          if (!row || !row[companyIdx]) continue
          
          const region = row[regionIdx] ? String(row[regionIdx]).trim() : ''
          const publishTime = timeIdx !== -1 ? formatExcelDate(row[timeIdx]) : ''
          const companyName = String(row[companyIdx]).trim()
          const herbsStr = row[herbIdx] ? String(row[herbIdx]).trim() : ''
          const herbs = herbsStr.split(/[、,，\s]+/).filter(h => h.length >= 2)
          const publisher = row[publisherIdx] ? String(row[publisherIdx]).trim() : ''
          
          importData.push({
            region,
            publish_time: publishTime,
            publisher,
            company_name: companyName,
            herb_names: herbs.join('、')
          })
        }
        
        if (importData.length === 0) {
          alert('Excel没有有效数据')
          return
        }
        
        // 调用后端批量导入API
        const res = await axios.post('/api/admin/xianxian-companies/batch-import', 
          { data: importData },
          { headers: { 'x-admin-password': getAdminPassword() } }
        )
        
        if (res.data?.code === 200) {
          alert(`导入成功！共 ${res.data.data?.imported || importData.length} 条`)
          this.fetchData()
        } else {
          alert('导入失败：' + res.data?.msg)
        }
      } catch (err) {
        console.error('Excel解析错误:', err)
        alert('Excel解析失败：' + err.message)
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

.main-content {
  flex: 1;
  margin-left: 260px;
  padding: 24px;
  overflow-y: auto;
  height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 26px;
  color: #2c3e50;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 16px;
}

.total-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.add-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.upload-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.4);
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.tab {
  padding: 10px 20px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  transition: all 0.3s ease;
}

.tab:hover {
  border-color: #667eea;
}

.tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: transparent;
}

.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  flex: 1;
  max-width: 400px;
  font-size: 14px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.content-body {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading, .error {
  text-align: center;
  padding: 50px;
  color: #666;
}

.error {
  color: #e74c3c;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #eee;
  font-size: 13px;
}

.data-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  font-size: 13px;
}

.data-table tr:hover {
  background: #f8f9fa;
}

.company-name {
  font-weight: 500;
  color: #2c3e50;
}

.region-tag, .province-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 12px;
}

.herb-tag {
  display: inline-block;
  padding: 4px 12px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.herb-names {
  display: inline-block;
  padding: 4px 10px;
  background: #f0fdf4;
  color: #166534;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  max-width: 400px;
  word-break: break-all;
}

.count-tag {
  display: inline-block;
  padding: 4px 10px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.actions {
  display: flex;
  gap: 8px;
}

.edit-btn {
  padding: 6px 12px;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.edit-btn:hover {
  background: #229954;
}

.delete-btn {
  padding: 6px 12px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.delete-btn:hover {
  background: #c82333;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.pagination button {
  padding: 8px 15px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 模态框 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
}

.modal-body {
  padding: 24px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.form-item {
  flex: 1;
}

.form-item.full-width {
  flex: 1 1 100%;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.form-item input, .form-item select, .form-item textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
}

.form-item textarea {
  min-height: 60px;
  resize: vertical;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.cancel-btn {
  padding: 10px 20px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.submit-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-hint {
  font-weight: normal;
  font-size: 12px;
  color: #999;
}
</style>